/**
 * BorrarReservaDialog — borrar una reserva con todo su movimiento financiero.
 *
 * Es la operación más destructiva del backoffice: se lleva pagos, vouchers,
 * viajeros, evidencias y la liquidación de comisión, y le devuelve los cupos a
 * la lancha. Por eso el diálogo pide dos cosas antes de habilitar el botón:
 * el motivo —que queda en la traza— y el código de la reserva escrito a mano.
 *
 * El servidor es quien decide si se puede: si la reserva está facturada, si su
 * saldo a favor ya se usó en otra, si tiene notas contables o comisión pagada,
 * responde 409 y acá se muestra tal cual. Una reserva embarcada no llega
 * siquiera a abrir este diálogo —el botón está deshabilitado—, así que su 409
 * se muestra como cualquier otro rechazo.
 */
import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Box, Typography,
  Button, TextField, Alert, Stack,
} from '@mui/material';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { API_BASE, getAuthHeaders, formatPrecio } from './shared/format';

export default function BorrarReservaDialog({ open, onClose, reserva, onBorrada, onToast }) {
  const codigo = reserva?.codigo_reserva || '';
  const [motivo, setMotivo] = useState('');
  const [confirmacion, setConfirmacion] = useState('');
  const [borrando, setBorrando] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setMotivo(''); setConfirmacion(''); setError(null);
  }, [open]);

  const pax = (reserva?.adultos || 0) + (reserva?.ninos || 0);
  const codigoOk = confirmacion.trim().toUpperCase() === codigo.toUpperCase();
  const puedeBorrar = codigoOk && motivo.trim().length >= 4 && !borrando;

  const borrar = async () => {
    setBorrando(true); setError(null);
    try {
      const r = await fetch(`${API_BASE}/turismo/correcciones/reservas/${reserva.id}/borrar`, {
        method: 'POST', headers: getAuthHeaders(),
        body: JSON.stringify({ motivo: motivo.trim() }),
      });
      const d = await r.json();
      if (!r.ok) {
        const det = d.detail;
        throw new Error(det?.message || det || 'No se pudo borrar');
      }
      onToast?.success?.(
        `${d.codigo_reserva} borrada. Se liberaron ${d.asientos_liberados} asiento(s).`);
      onBorrada?.(d);
      onClose?.();
    } catch (e) {
      setError(e.message || 'No se pudo borrar');
    }
    setBorrando(false);
  };

  return (
    <Dialog open={!!open} onClose={borrando ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 800, fontSize: 17, color: '#b91c1c' }}>
        Borrar la reserva
        <Typography sx={{ fontSize: 12.5, color: '#64748b', fontWeight: 500 }}>
          {codigo} · {reserva?.nombre_cliente}
        </Typography>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2, whiteSpace: 'pre-line' }}>
            {error}
          </Alert>
        )}

        <Stack spacing={2} sx={{ mt: 0.5 }}>
          {/* Qué se va a llevar por delante, en plata y en cupos */}
          <Box sx={{ p: 1.5, borderRadius: 1, bgcolor: '#fef2f2', border: '1px solid #fecaca' }}>
            <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: '#b91c1c',
                              textTransform: 'uppercase', letterSpacing: 0.3, mb: 0.75 }}>
              Esto se deshace
            </Typography>
            <Typography sx={{ fontSize: 12, color: '#7f1d1d' }}>
              • Sus pagos, vouchers, viajeros y evidencias de check-in<br />
              • La comisión de la agencia y su liquidación<br />
              • {pax} asiento(s) vuelven a quedar libres en la salida<br />
              {Number(reserva?.saldo_pendiente || 0) > 0 && (
                <>• El saldo de {formatPrecio(reserva.saldo_pendiente, reserva.moneda)} deja de existir<br /></>
              )}
            </Typography>
            <Typography sx={{ fontSize: 11, color: '#7f1d1d', mt: 0.75, fontStyle: 'italic' }}>
              Queda un script que la restaura completa, por si hubo un error.
            </Typography>
          </Box>

          <TextField label="Por qué se borra" size="small" value={motivo} multiline minRows={2}
                     onChange={(e) => setMotivo(e.target.value)}
                     placeholder="Quedó mal montada, duplicada, el cliente desistió…"
                     helperText="Queda en la traza de auditoría" />

          <TextField label={`Escribe ${codigo} para confirmar`} size="small" value={confirmacion}
                     onChange={(e) => setConfirmacion(e.target.value)}
                     error={confirmacion.length > 0 && !codigoOk}
                     inputProps={{ autoCapitalize: 'characters', autoComplete: 'off', spellCheck: false }} />
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={borrando} sx={{ textTransform: 'none' }}>
          Cancelar
        </Button>
        <Button variant="contained" color="error" startIcon={<DeleteForeverIcon />}
                disabled={!puedeBorrar} onClick={borrar}
                sx={{ textTransform: 'none', fontWeight: 700 }}>
          {borrando ? 'Borrando…' : 'Borrar definitivamente'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
